import { classifyAppSyncFrame, markSubscriptionDelivery, markSubscriptionInbound } from "utils/subscriptionLiveness";

// Identify the AppSync realtime WebSocket. AWS-hosted endpoints carry "appsync-realtime-api" in the host
// (data uses "appsync-api"); behind a CUSTOM DOMAIN (e.g. wss://*.client.api.jidlomat.dev/graphql/realtime)
// there is no appsync-* host, so the realtime endpoint is identified by its "/graphql/realtime" path. Match
// any of these so a custom domain doesn't silently disable the tap — while still excluding the Vite HMR
// dev socket (ws://localhost/?token=...), whose frequent traffic would otherwise mask a dead AppSync stream.
const isAppSyncUrl = (url: string): boolean =>
	url.includes("appsync-realtime-api") || url.includes("appsync-api") || url.includes("/graphql/realtime");

let installed = false;

/**
 * Wrap the global WebSocket ONCE so we can OBSERVE inbound AppSync messages — including `ka` keepalives,
 * which aws-appsync-subscription-link consumes internally and never surfaces to the Apollo link chain.
 * We only attach a passive listener; we never consume or send. Must run before the Apollo client opens
 * any socket (call from main.tsx).
 */
export const installSubscriptionLivenessTap = (): void => {
	if (installed || typeof globalThis === "undefined" || !globalThis.WebSocket) {
		return;
	}
	installed = true;
	const NativeWebSocket = globalThis.WebSocket;
	const Tapped = function (this: unknown, url: string | URL, protocols?: string | string[]) {
		const socket = new NativeWebSocket(url, protocols);
		const href = typeof url === "string" ? url : url.toString();
		if (isAppSyncUrl(href)) {
			socket.addEventListener("message", (event) => {
				// Every frame (incl. `ka`) refreshes connection liveness; only real delivery / re-registration
				// frames (data, start_ack, connection_ack) count as delivery evidence that can clear a broken flag.
				markSubscriptionInbound();
				if (classifyAppSyncFrame(event.data) === "delivery") {
					markSubscriptionDelivery();
				}
			});
		}
		return socket;
	} as unknown as typeof WebSocket;
	Tapped.prototype = NativeWebSocket.prototype;
	// Preserve the static CONNECTING/OPEN/CLOSING/CLOSED constants.
	Object.assign(Tapped, NativeWebSocket);
	globalThis.WebSocket = Tapped;
};
